import type { JSXConvertersFunction } from '@payloadcms/richtext-lexical/react'
import type { Media } from '@/payload-types'
import { mediaUrl } from '@/lib/seo/jsonld'
import { imageCaption } from '@/lib/mediaCaption'
import { isCitationSup } from '@/lib/citations'

/**
 * IS_HIGHLIGHT di Lexical, cioè il bit che `<mark>` lascia sul nodo di testo.
 *
 * Si scrive a mano perché il pacchetto non lo esporta da un percorso pubblico
 * (`@payloadcms/richtext-lexical/dist/lexical/utils/nodeFormat` non è un export). Il valore è
 * `1 << 7` e sta accanto ai bit che si vedono già in tabella: 1 = grassetto, 2 = corsivo, 64 = apice.
 */
const IS_HIGHLIGHT = 1 << 7

/**
 * Converters per <RichText> dell'articolo. Tre override, e nient'altro.
 *
 * `text` — DUE bit oltre a quelli che il converter di default gestisce già:
 *
 *  - APICE DI CITAZIONE (64): NON SI RENDE. In pagina non ci sono più apici: la tracciabilità sta
 *    nell'elenco delle fonti in coda all'articolo, con il numero di informazioni estratte per
 *    ognuna (<SourcesBlock>), e venti-trenta numeretti in mezzo alle frasi erano il rumore che
 *    quell'elenco esiste per evitare. Il contenuto già in tabella li porta ancora (post 62,
 *    `format=64`, testo `[3]`) e senza questo caso uscirebbero come «...del Fiora.[3]» a misura
 *    piena, cioè peggio dell'apice che sostituivano — vedi src/lib/citations.ts, che spiega anche
 *    perché la riparazione sta in resa e non in tabella.
 *  - EVIDENZIAZIONE (128): il converter di default legge sette bit di formato e IGNORA questo. È
 *    quello che serve alle TESTATE CITATE NEL CORPO — `markSourceMentions` le avvolge in `<mark>`,
 *    la conversione a Lexical le porta a `format=128` e qui diventano un `<cite>`, il tag semantico
 *    del nome di un'opera o di una testata. Vale per i nomi che la prosa cita per CONTENUTO (una
 *    dichiarazione con il nome di chi parla, un fatto che parla della fonte stessa), che sono gli
 *    unici due casi che il prompt ammette da quando il disaccordo fra fonti si risolve a monte.
 *
 * Il default si CHIAMA, non si riscrive: così grassetto e corsivo dentro un nome citato continuano a
 * funzionare, e un bit nuovo aggiunto dal pacchetto arriva in pagina da solo.
 *
 * `upload`: quello di default usa l'URL LOCALe del media (uploadDoc.url), ma qui i media vivono su
 * Bunny — coerentemente con la featured image usiamo mediaUrl() (preferisce bunnyUrl).
 *
 * DIDASCALIA E ALT VENGONO PRIMA DAL NODO, poi dal media. I due campi del nodo li dichiara
 * UploadFeature in src/payload.config.ts e stanno nel jsonb del contenuto: sono la didascalia
 * DI QUESTO INSERIMENTO, non del file, quindi la stessa foto può accompagnare due articoli con
 * due didascalie diverse. Sui nodi entrati col backfill delle immagini di WordPress (agosto 2026)
 * `node.fields` è vuoto e la resa è quella di prima.
 *
 * La dichiarazione sull'immagine generata da un modello la mette imageCaption()
 * (src/lib/mediaCaption.ts), la stessa funzione della copertina: prima non c'era su nessuna foto
 * del corpo, cioè mancava esattamente dove una foto inventata si legge come una fotografia.
 */
export const articleConverters: JSXConvertersFunction = ({ defaultConverters }) => ({
  ...defaultConverters,
  text: (args) => {
    const format = Number(args.node.format) || 0
    const rendered = (defaultConverters.text as (a: typeof args) => React.ReactNode)(args)

    // Un apice che non è una citazione (un «m2», un «1º» scritti in apice a mano nell'admin) resta
    // l'apice che il converter di default rende: qui si tocca solo ciò che si sa leggere.
    if (isCitationSup(format, String(args.node.text ?? ''))) return null

    if (!(format & IS_HIGHLIGHT)) return rendered
    return (
      // `not-italic` perché il browser corsiva `<cite>` da solo, e un corsivo qui si confonderebbe
      // con l'enfasi del testo. La tinta è quella d'accento del tenant, la stessa dei chip
      // istituzionali di <SourcesBlock>: il lettore ritrova in fondo alla pagina il nome che ha
      // visto nella frase. Il tratteggio sotto dice «rimando» senza promettere un link, che qui non
      // c'è (convertHTMLToLexical scarta rel="nofollow", quindi nel corpo non si linka).
      <cite className="font-medium not-italic text-accent-strong decoration-accent/40 underline decoration-dotted underline-offset-2">
        {rendered}
      </cite>
    )
  },
  upload: ({ node }) => {
    const doc = node?.value
    if (!doc || typeof doc !== 'object') return null
    const media = doc as Media
    const src = mediaUrl(media)
    if (!src) return null
    const fields = (node.fields ?? {}) as {
      alt?: string | null
      caption?: string | null
      displayWidth?: number | null
    }
    const caption = imageCaption(media, fields.caption)
    // Misura per inserimento, nel JSON Lexical: il file può restare a tutta colonna altrove.
    // I media senza una misura esplicita mantengono il layout delle fotografie esistenti.
    const displayWidth =
      typeof fields.displayWidth === 'number' && Number.isFinite(fields.displayWidth) && fields.displayWidth > 0
        ? Math.max(120, Math.min(768, fields.displayWidth))
        : undefined
    // L'alt NON è la didascalia con la nota d'uso attaccata: chi ascolta la pagina deve sentire che
    // cosa si vede, e la dichiarazione la sente comunque perché la <figcaption> è testo in pagina.
    const alt = fields.alt?.trim() || media.alt || fields.caption?.trim() || media.caption || ''
    return (
      <figure className="my-8" style={displayWidth ? { maxWidth: displayWidth, marginInline: 'auto' } : undefined}>
        <img
          src={src}
          alt={alt}
          width={media.width ?? undefined}
          height={media.height ?? undefined}
          loading="lazy"
          decoding="async"
          className="w-full rounded-card"
        />
        {caption ? <figcaption className="mt-2 text-center text-sm text-zinc-500">{caption}</figcaption> : null}
      </figure>
    )
  },
})
